/**
 * Drag-to-resize width for a pane, with clamped localStorage persistence.
 * Ported from the Labs console. Keep column components module-scoped so the
 * ~60fps setWidth during a drag doesn't remount them (and lose scroll).
 *
 * Two options for a pane anchored to the *right* edge — the Desk item panel —
 * where the grab strip sits on its left and dragging left makes it wider:
 * `invert` flips the drag direction, and `onEnd` reports the settled width so
 * the caller can decide the panel has been pulled far enough to become the
 * page. Neither changes the Miller-column callers, which pass neither.
 */

import { useCallback, useEffect, useState, type PointerEvent as ReactPointerEvent } from "react";

export interface ResizableWidthOptions {
  /** Dragging left grows the pane (right-anchored). */
  invert?: boolean;
  /** Called once when the drag ends, with the width it settled at. */
  onEnd?: (width: number) => void;
}

export function useResizableWidth(
  key: string,
  initial: number,
  min: number,
  max: number,
  options: ResizableWidthOptions = {},
) {
  const { invert = false, onEnd } = options;
  const [width, setWidth] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(key));
      return v >= min && v <= max ? v : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, String(width));
    } catch {
      /* storage blocked (private mode) — width just isn't persisted */
    }
  }, [key, width]);

  const onResizeStart = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      const sign = invert ? -1 : 1;
      let last = startW;
      const move = (ev: PointerEvent) => {
        last = Math.min(max, Math.max(min, startW + sign * (ev.clientX - startX)));
        setWidth(last);
      };
      const up = () => {
        document.removeEventListener("pointermove", move);
        document.removeEventListener("pointerup", up);
        document.body.style.removeProperty("cursor");
        document.body.style.removeProperty("user-select");
        onEnd?.(last);
      };
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width, min, max, invert, onEnd],
  );

  return { width, onResizeStart };
}
